import React, { useEffect, useState } from 'react';
import { i18n } from 'i18next';
import data, { EmojiMartData } from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

import useAppContext from 'flow/hooks/useAppContext';
import { flip, useFloating } from '@floating-ui/react';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { twMerge } from 'tailwind-merge';

interface Props {
    onEmojiSelect?: (emoji: string) => void;
    keepOpen: boolean;
    /** Dark variant, see MessagesPage. Also forces the emoji-mart popover
     *  into its dark theme, since that library takes it as a plain JS prop
     *  rather than reading it from CSS. */
    videoDark?: boolean;
}

const EmojiPicker = ({ onEmojiSelect, keepOpen, videoDark = false }: Props) => {
    const { i18next } = useAppContext();
    const [show, setShow] = useState(false);

    const { refs, floatingStyles } = useFloating({
        placement: 'bottom-end',
        middleware: [flip()],
    });

    useEffect(() => {
        if (keepOpen === false) {
            setShow(false);
        }
    }, [keepOpen]);

    return (
        <>
            <div ref={refs.setReference}>
                <IconButton
                    className={twMerge(
                        'size-9 rounded-sm',
                        videoDark
                            ? 'text-gray-300 hover:bg-gray-600'
                            : 'text-gray-600 hover:bg-gray-200',
                    )}
                    onClick={(event) => {
                        event.stopPropagation();
                        setShow((prev) => !prev);
                    }}
                    tooltipLabel={i18next.t('reactions.modal.add')}
                >
                    <Icon iconKey="addReaction" size="1.2rem" />
                </IconButton>
            </div>

            {show && (
                <div
                    className="z-40 py-4"
                    ref={refs.setFloating}
                    style={floatingStyles}
                    // The picker has its own keyboard handling: the message
                    // action bar's focusgroup and the message list must not
                    // reach into it.
                    focusgroup="none"
                    data-roving-ignore=""
                >
                    <Picker
                        className="shadow-base rounded-sm"
                        i18n={translations(i18next.t)}
                        data={data}
                        theme={
                            videoDark ||
                            localStorage.getItem('theme') === 'dark'
                                ? 'dark'
                                : 'light'
                        }
                        onEmojiSelect={(
                            emoji: EmojiMartData & { shortcodes: string },
                        ) => {
                            onEmojiSelect(emoji.shortcodes);
                            setShow(false);
                        }}
                        onClickOutside={() => setShow(false)}
                        previewPosition="none"
                    />
                </div>
            )}
        </>
    );
};

const translations = (t: i18n['t']) => ({
    search: t('search'),
    search_no_results_1: t('search_no_results_1'),
    search_no_results_2: t('search_no_results_2'),
    pick: t('pick'),
    add_custom: t('add_custom'),
    categories: {
        activity: t('activity'),
        custom: t('custom'),
        flags: t('flags'),
        foods: t('foods'),
        frequent: t('frequent'),
        nature: t('nature'),
        objects: t('objects'),
        people: t('people'),
        places: t('places'),
        search: t('search_result'),
        symbols: t('symbols'),
    },
    skins: {
        choose: t('choose'),
        '1': t('skins_default'),
        '2': t('skins_light'),
        '3': t('skins_medium-light'),
        '4': t('skins_medium'),
        '5': t('skins_medium-dark'),
        '6': t('skins_dark'),
    },
});

export default EmojiPicker;
